import React, { useCallback, useEffect } from 'react'
import { Controller, FieldValues, useFormContext } from 'react-hook-form'

import { FormControlProps, ComponentProps, OmittedFormControlProps } from './controls.types.ts'

export const FormControl = ({
  name,
  unregister: triggerUnregister,
  component: Component,
  props,
  revalidateFields,
  normalize,
}: FormControlProps): React.ReactElement => {
  const { unregister, control, formState, trigger } = useFormContext()
  const { errors, isSubmitting } = formState

  const handleControlChange = useCallback(
    (onChange: (value: any) => void) => (event: any) => {
      if (normalize) {
        const changedValue = typeof event === 'string' ? { target: { value: event } } : event
        const { target } = changedValue as React.ChangeEvent<HTMLInputElement>
        onChange(normalize(target.value))
      } else {
        onChange(event)
      }

      if (revalidateFields) {
        void trigger(revalidateFields)
      }
    },
    [normalize, trigger, revalidateFields]
  )

  useEffect(
    () => () => {
      if (triggerUnregister) {
        unregister(name)
      }
    },
    [triggerUnregister]
  )

  const splitName = name.split('.')
  const [basename, index, key] = splitName
  const isFieldArrayItem = splitName.length >= 2

  const flatError = props?.error || errors[name]?.message
  const arrayError = props?.error || ((errors as Record<string, any>)[basename]?.[index]?.[key]?.message as string)
  const hasError = Boolean(isFieldArrayItem ? arrayError : flatError)

  const controlError = isFieldArrayItem ? arrayError : flatError

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Component
          {...field}
          {...props}
          onChange={handleControlChange(field.onChange)}
          disabled={(props?.disabled as boolean) || isSubmitting}
          error={hasError}
          helperText={hasError ? controlError : props?.helperText}
        />
      )}
    />
  )
}

export const createFormControl =
  <ControlProps extends ComponentProps>(component: React.ComponentType<ControlProps>) =>
  <FormValues extends FieldValues>({
    name,
    revalidateFields,
    normalize,
    unregister,
    ...props
  }: Omit<ControlProps, 'onChange'> & OmittedFormControlProps<FormValues>) => (
    <FormControl
      component={component}
      props={props}
      name={name}
      revalidateFields={revalidateFields}
      normalize={normalize}
      unregister={unregister}
    />
  )
